// tslint:disable:max-file-line-count
/* eslint-disable max-lines */

import { inject, observer } from 'mobx-react';
import { AnalyticsStore, ExperimentStore } from 'bernie-plugin-mobx';
import { computed, IObservableArray } from 'mobx';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { Layer } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { QueryRoute, updateSearch } from 'bernie-core';

import { ChildModel } from 'stores/models/child-model';
import { MultiItemStore } from 'stores/multi-item-store';
import { RoomModel } from 'stores/models/room-model';

import { classNames } from 'uitk-react-utils';
import { UitkFauxInput, UitkTextInput } from 'uitk-react-input';

import { FormFieldBinding } from './form-field-binding';
import { PropertyFilterAmenity } from 'src/common/__generated__/api';
import { RoomConfig, RoomPickerModel } from 'components/common/room-picker/room-picker-model';
import { RoomPicker } from 'components/common/room-picker/room-picker';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface TravelersProps extends LocalizedComponentProps, SearchFormFieldProps, RouteComponentProps {
  analytics?: AnalyticsStore;
  buttonText?: string;
  dismissOnSave?: boolean;
  hidden?: boolean;
  id?: string;
  name?: string;
  onChange?: any;
  placeholder?: string;
  hideRoomsInDisplaySummary?: boolean;
  multiItem?: MultiItemStore;
  experiment?: ExperimentStore;
}

export interface TravelersState {
  adults: string;
  children: string;
  mounted: boolean;
  value: string;
  infantsInSeats?: string;
}

@withRouter
@withLocalization
@FormFieldBinding
@inject('analytics', 'multiItem', 'experiment')
@observer
export class Travelers extends React.Component<TravelersProps, TravelersState> {
  public focusButton: any = React.createRef();
  private roomPickerModel: RoomPickerModel;
  private isPackageShoppingPath: boolean;
  constructor(props) {
    super(props);
    const guestCount = this.props.model?.guestCount?.toString();

    this.state = {
      adults: guestCount,
      children: '',
      mounted: false,
      value: '',
    };

    this.isPackageShoppingPath = props.multiItem && props.multiItem.isPackageShoppingPath();
    this.roomPickerModel = new RoomPickerModel({ adults: '', children: '' }, props.l10n, props.experiment, this.isPackageShoppingPath);

    this.convertRoomConfigToRoomModel = this.convertRoomConfigToRoomModel.bind(this);
    this.convertRoomModelToRoomConfig = this.convertRoomModelToRoomConfig.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.open = this.open.bind(this);
    this.openDatepicker = this.openDatepicker.bind(this);
    this.save = this.save.bind(this);
    this.validate = this.validate.bind(this);
    this.parseRooms = this.parseRooms.bind(this);
    this.updatePetsSelection = this.updatePetsSelection.bind(this);
    props.registerValidator(() => this.validate('children'));
    props.registerValidator(() => this.validate('adults'));
  }

  public static defaultProps = {
    dismissOnSave: true,
    l10n: {},
  };

  private open() {
    const { analytics, history, location } = this.props;

    updateSearch({ history, location, newParams: { pwaDialog: 'travelerPicks' } });

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':ChangeRoomGuest', 'Open Room Picker', null);
    }
  }

  private openDatepicker() {
    const { analytics, history, location } = this.props;

    updateSearch({ history, location, newParams: { pwaDialog: 'datepicker-start' } });

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':OpenDatepickerRoomGuest', 'Open Datepicker from Room Guest', null);
    }
  }

  private save(adults: string, children: string, value: string, petsIncluded: boolean, infantsInSeats?: string) {
    const { model, dismissOnSave, updateModel, experiment } = this.props;

    if (dismissOnSave) {
      this.dismiss();
    }

    this.roomPickerModel.setupRooms(adults, children);

    const { finalProps, rooms } = this.roomPickerModel;

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name: 'rooms',
          value: this.convertRoomConfigToRoomModel(rooms),
        },
      });

      if (infantsInSeats) {
        updateModel({
          target: {
            name: 'infantsInSeats',
            value: infantsInSeats,
          },
        });
      }

      if (experiment?.exposures?.Vrbex_generic?.bucket === 1) {
        let amenities = [...model.amenities];
        const index = amenities.indexOf(PropertyFilterAmenity.PETS);
        if (petsIncluded && index < 0) {
          amenities.push(PropertyFilterAmenity.PETS);
        } else if (!petsIncluded && index > -1) {
          amenities = amenities.filter(amenity => amenity !== PropertyFilterAmenity.PETS);
        }
        updateModel({
          target: {
            name: 'amenities',
            value: amenities,
          },
        });
      }
    }

    this.setState({
      adults: finalProps.adults,
      children: finalProps.children,
      value,
      infantsInSeats,
    });
  }

  private dismiss() {
    this.props.history.goBack();
    // manage focus back to the trigger
    const focusButton = this.focusButton;
    if (focusButton.current && typeof focusButton.current.focus === 'function') {
      focusButton.current.focus();
    }
  }

  private convertRoomModelToRoomConfig(rooms: RoomModel[]): IObservableArray<RoomConfig> {
    return rooms.map((room: RoomModel) => ({
      adults: room.adults,
      children: room.children.map((child: ChildModel) => child.age),
    })) as IObservableArray<RoomConfig>;
  }

  private convertRoomConfigToRoomModel(rooms: IObservableArray<RoomConfig>): RoomModel[] {
    return rooms.map(
      (room: RoomConfig) =>
        new RoomModel({
          adults: room.adults,
          children: room.children.map((child: number) => new ChildModel({ age: child })),
        })
    );
  }

  private parseRooms(rooms) {
    if (!rooms) {
      return;
    }

    this.roomPickerModel.rooms = this.convertRoomModelToRoomConfig(rooms);
    const { displaySummary, finalProps } = this.roomPickerModel;

    return {
      adults: finalProps.adults,
      children: finalProps.children,
      value: displaySummary,
    };
  }

  @computed
  get petsIncluded(): boolean {
    return this.props.model?.amenities?.includes(PropertyFilterAmenity.PETS) || false;
  }

  private updatePetsSelection() {
    this.petsIncluded ? this.roomPickerModel.includePets() : this.roomPickerModel.excludePets();
  }

  public componentDidMount() {
    const { rooms, infantsInSeats } = this.props.model;

    this.updatePetsSelection();
    this.setState({
      ...this.parseRooms(rooms),
      mounted: true,
      infantsInSeats,
    });
  }

  componentDidUpdate() {
    this.updatePetsSelection();
  }

  public componentWillReceiveProps(nextProps) {
    const { rooms, infantsInSeats } = nextProps.model;
    this.setState({
      ...this.parseRooms(rooms),
      infantsInSeats,
    });
  }

  private getLabel() {
    return this.isPackageShoppingPath ? 'hotels.travelers.multiitem.dialog.label' : 'hotels.search.form.travelers.label';
  }

  public render() {
    const { id, name, placeholder, updateModel, hidden, wrapperClassNames, multiItem } = this.props;
    const { adults, mounted, value } = this.state;

    const idBtn = `${id}__btn`;
    const adjustedAdults = parseInt(adults, 10) > RoomPickerModel.MAX_GUESTS ? RoomPickerModel.MAX_GUESTS.toString() : adults;
    // If mounted, use summary, otherwise just use adults=
    const renderedValue = mounted ? value : adjustedAdults;
    const buttonText = this.props.buttonText;

    const renderLabel = () => (
      <>
        <LocalizedText message={this.getLabel()} />
        <span className="is-visually-hidden">{renderedValue}</span>
      </>
    );

    const inputClassNames: string[] = [];

    if (hidden) {
      return this.fieldValues(this.state);
    }

    if (mounted) {
      inputClassNames.push('is-hidden');
    }

    const isFlightInSearchForm = multiItem?.multiItemContext?.packageType?.includes('F');
    const itemClass = classNames({
      'uitk-cell s-cell-1-1 m-cell-1-2 l-cell-1-3 xl-cell-1-3': isFlightInSearchForm,
      'uitk-cell s-cell-1-1 m-cell-1-4 l-cell-1-6 xl-cell-1-6': !isFlightInSearchForm,
    });

    return (
      <div className={wrapperClassNames || itemClass}>
        <UitkTextInput
          id={id}
          name={mounted ? name : 'adults'}
          className={inputClassNames.join(' ')}
          label={renderLabel()}
          placeholder={placeholder}
          value={renderedValue}
          onChange={updateModel}
          data-stid="input-date"
          icon="person"
          invalid={mounted && (this.validate('adults') || this.validate('children'))}
        >
          {mounted && (
            <UitkFauxInput domRef={this.focusButton} id={idBtn} type="button" data-stid="open-room-picker" onClick={this.open}>
              <span className="is-visually-hidden">
                <LocalizedText message={this.getLabel()} />{' '}
              </span>
              {renderedValue}
            </UitkFauxInput>
          )}
        </UitkTextInput>
        {mounted && this.fieldValues(this.state)}
        <QueryRoute query={{ pwaDialog: 'travelerPicks' }}>
          <Layer id="home-travelerpicks" onDismiss={this.dismiss}>
            <RoomPicker
              adultsString={this.state.adults}
              childrenString={this.state.children}
              petsIncluded={this.petsIncluded}
              infantsInSeats={this.state.infantsInSeats}
              onSave={this.save}
              onDismiss={this.dismiss}
              analytics={this.props.analytics}
              buttonText={buttonText}
              isPackageShoppingPath={this.isPackageShoppingPath}
            />
          </Layer>
        </QueryRoute>
      </div>
    );
  }

  private fieldValues = (state: TravelersState) => {
    const { adults, children, infantsInSeats } = state;

    return (
      <React.Fragment>
        {adults && <input type="hidden" name="adults" value={adults} readOnly={true} />}
        {children && <input type="hidden" name="children" value={children} readOnly={true} />}
        {infantsInSeats && <input type="hidden" name="infantsInSeats" value={infantsInSeats} readOnly={true} />}
      </React.Fragment>
    );
  };

  private validate(key) {
    const { l10n } = this.props;
    const { adults, children } = this.state;
    const { formatText } = l10n;

    const numAdults = adults
      .split(',')
      .map(parseInt)
      .reduce((a, b) => a + b);
    if (key === 'adults') {
      if (!adults) {
        return formatText('hotels.search.validation.invalidGuests.text');
      }
      if (numAdults <= 0) {
        return formatText('hotels.search.validation.invalidGuests.text');
      }
    }

    if (key === 'children') {
      if (!children) {
        return;
      }

      const childAges = children.split(',');
      const infantsArr = childAges.filter(age => parseInt(age.split('_')[1], 10) < 2);

      let message =
        childAges.length === 1
          ? formatText('hotels.search.validation.missingChildAge.text')
          : formatText('hotels.search.validation.missingChildrenAges.text');

      let error = false;

      const childAgeOnly = [];
      childAges.forEach(child => {
        if (!child.match(/\d{1,2}_\d{1,2}/)) {
          // i.e. roomNumber_childAge, e.g. 1_4
          error = true;
        }
        childAgeOnly.push(parseInt(child.split('_')[1], 10));
      });

      const supervisors = childAgeOnly.filter(age => age >= 6).length + numAdults;
      if (this.isPackageShoppingPath && infantsArr.length > supervisors) {
        error = true;
        message = formatText('hotels.travelers.multiitem.validation.maxInfants');
      }

      return error ? message : undefined;
    }
  }
}
